import React,{useEffect,useLayoutEffect,useRef,useState} from 'react';
import {guestRequest} from '../services/guestApi';
import {usePhotoNavigationGuard} from '../services/photos/usePhotoNavigationGuard';
import {Button,Card} from './ui';

type Config={historyLimit:number;revision:string;minLimit:number;maxLimit:number};
export default function UserManagementConfig({onBusyChange}:{onBusyChange?:(busy:boolean)=>void}){
 const [saved,setSaved]=useState<Config|null>(null),[value,setValue]=useState('');
 const [loading,setLoading]=useState(true),[busy,setBusy]=useState(false),[error,setError]=useState(''),[message,setMessage]=useState(''),[reload,setReload]=useState(0);
 const mounted=useRef(false),inFlight=useRef(false);
 const dirty=!!saved&&value!==String(saved.historyLimit);
 usePhotoNavigationGuard(dirty||busy,'Leave without saving user configuration changes?');
 useLayoutEffect(()=>{onBusyChange?.(busy);return()=>onBusyChange?.(false);},[busy,onBusyChange]);
 useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
 useEffect(()=>{
  const controller=new AbortController();setLoading(true);setError('');
  void guestRequest<Config>('/user-management/config','GET',undefined,controller.signal).then(result=>{
   if(controller.signal.aborted)return;setSaved(result);setValue(String(result.historyLimit));
  }).catch(e=>{if(!controller.signal.aborted)setError(e.message);}).finally(()=>{if(!controller.signal.aborted)setLoading(false);});
  return()=>controller.abort();
 },[reload]);
 const valid=!!saved&&/^\d+$/.test(value)&&Number(value)>=saved.minLimit&&Number(value)<=saved.maxLimit;
 const save=async(e:React.FormEvent)=>{
  e.preventDefault();if(!saved||!valid||inFlight.current)return;
  inFlight.current=true;setBusy(true);setError('');setMessage('');
  try{
   const result=await guestRequest<Config>('/user-management/config','PUT',{historyLimit:Number(value),revision:saved.revision});
   if(mounted.current){setSaved(result);setValue(String(result.historyLimit));setMessage('User configuration saved.');}
  }catch(e){if(mounted.current)setError((e as Error).message);}
  finally{inFlight.current=false;if(mounted.current)setBusy(false);}
 };
 return <Card className="p-5 sm:p-6 space-y-4">
  <h3 className="font-semibold">Config</h3>
  <p className="text-sm text-gray-500">Settings apply to every user across the workspace.</p>
  {loading&&<p role="status">Loading configuration…</p>}
  {error&&<div role="alert" className="text-sm text-red-700">{error}<button type="button" disabled={busy||loading} className="block underline min-h-11" onClick={()=>{
   if(dirty&&!window.confirm('Discard your changes and reload saved configuration?'))return;setMessage('');setReload(n=>n+1);
  }}>Reload saved config</button></div>}
  {saved&&!loading&&<form onSubmit={save} className="space-y-4">
   <label className="block text-sm font-medium">Activity entries per user
    <input type="number" inputMode="numeric" min={saved.minLimit} max={saved.maxLimit} step={1} required value={value} disabled={busy} aria-describedby="history-limit-help"
     className="block mt-2 border rounded-lg p-3 w-full sm:w-64 text-base" onChange={e=>{setValue(e.target.value);setMessage('');}}/>
   </label>
   <p id="history-limit-help" className="text-sm text-gray-500">Keep the newest {saved.minLimit.toLocaleString()}–{saved.maxLimit.toLocaleString()} activity entries for each user. Lowering this limit permanently removes older entries when you save. Increasing it retains more future activity; removed entries cannot be restored.</p>
   {dirty&&!valid&&<p className="text-sm text-red-700">Enter a whole number from {saved.minLimit.toLocaleString()} to {saved.maxLimit.toLocaleString()}.</p>}
   <div className="border-t pt-4 flex flex-wrap items-center justify-between gap-3">
    <p className="text-sm text-gray-500">{dirty?'Unsaved changes':''}</p>
    <Button type="submit" disabled={busy||!dirty||!valid}>{busy?'Saving…':'Save config'}</Button>
   </div>
  </form>}
  {message&&<p role="status" className="text-sm text-usda-700">{message}</p>}
 </Card>;
}
